// World weather map — frameworkless frontend over MapLibre GL JS.
// Loads a manifest of daily/time-of-day frames and animates them.
import { VARIABLES, PALETTES, colorExpression, colorExpressionOn, gradientCss, clippedGradientCss, domain, bandScale, colorAt } from './palettes.ts';
import { VAR, CATEGORIES, VARIANT_GROUPS } from '../lib/variables.ts';
import { DEFAULT_RECIPE, PRESETS, clampRecipe, encodeRecipe, decodeRecipe, evalRecipe, factorsForRecipe } from '../lib/recipe.ts';
import type { RecipeInput } from '../lib/recipe.ts';
import { buildOceanMask } from '../lib/ocean-mask.ts';
import type { LandCollection } from '../lib/ocean-mask.ts';
import { sectionLabel, sectionKeyForMmdd } from '../lib/sections.ts';
import { yearsPhraseCap } from '../lib/year-span.ts';
import {
  MONTH_FULL, MONTH_ABBR, MONTH_INITIAL,
  monthlyMeans, meanOf, extentIdx, cumulativeRain, driverState, placeLabel, popLabel,
} from '../lib/city-stats.ts';
import type { CityProps, CitySeries, CityHint, CityHit, SelectedCity } from '../contracts/city.ts';
import { parseRoute, keyForDateSlug, varForModeSlug } from '../lib/slug.ts';
import {
  routePathFor, routeKeyFor, flagsForIncomingRoute, isHistoryPush, roundCamera, NEUTRAL_FLAGS,
} from '../lib/route-state.ts';
import type { RouteFlags, RouteInputs } from '../lib/route-state.ts';
import { initMyDay, refreshPeaks, syncPeaksChrome, rerenderPeaks, setPeaksOpen, copyLink } from './myday.ts';
import { initDaySentence, renderDayMenu, wireDaySentence } from './ui/day-sentence.ts';
import { initSearchMenu, openSearch as openSearchPanel, renderSearchMenu, wireSearchMenu } from './ui/search-menu.ts';
import { initCompare, openCompare, addToComparison, startCompareFrom, placeFrom, renderTray, wireCompare, toggleInComparison, renderCompare } from './compare/view.ts';
import type { FeatureCollection, GeoJSON, Point } from 'geojson';
import type {
  ExpressionSpecification,
  FilterSpecification,
  GeoJSONSource,
  LayerSpecification,
  Map as MapLibreMap,
  MapMouseEvent,
  MapSourceDataEvent,
  StyleSpecification,
} from 'maplibre-gl';
import type { Manifest } from '../lib/manifest.ts';
import type { Conditions, Factor, Gate, Recipe, WeightKey, Weights } from '../lib/recipe.ts';
import type { VarKey, VariantGroup } from '../lib/variables.ts';

import { $ } from './dom.ts';
import { store, state, EMPTY_FC, emptyCities, OCEAN_COLOR, DEFAULT_FIELD_OPACITY } from './state.ts';
import { map, setMap, hasMap } from './map/instance.ts';
import { dotFilter, labelFilter } from './map/filters.ts';
import { updateLegend, updateArrowLegend } from './ui/legend.ts';
import { buildRuler, updateRulerMarker, updateRulerSweep, wireRuler } from './ui/ruler.ts';
import { pause, togglePlay, wirePlayback } from './ui/playback.ts';
import { setupHover, positionRing, activityBlock } from './ui/tooltip.ts';
import { wireAutocomplete } from './ui/autocomplete.ts';
import {
  initCityPanel, openCity, reloadSeries, computeCustomSeries, updateCityCharts, buildCalendar,
} from './city/panel.ts';
import {
  initLayout, setView, goMap, goLanding, wireLayout,
} from './ui/layout.ts';
import {
  initChrome, closeLayerMenu, renderLayerMenu, activityKeys, wireChrome, unlockCustom, renderCornerWords,
} from './ui/chrome.ts';
import { initRecipeEditor, toggleCustomEditor, syncCustomEditor, wireCustomEditor } from './ui/recipe-editor.ts';
import { initSentence, renderSentence, renderMenus, wireSentence } from './ui/sentence.ts';
import { initLensMenu, renderLensMenu } from './ui/lens-menu.ts';
import {
  initUrlSync, syncUrl, applyRouteFromUrl, markUserCamera, setApplyingRoute,
  markDateExplicit, markModeExplicit, wireHistory,
} from './routing/url-sync.ts';
import { track, trackDebounced } from './analytics.ts';
import { addOceanMask, addCountryLabels, addBasemapDetailLayers, addWeatherLayers, addCityLabels, setSelectedHighlight } from './map/layers.ts';
import { loadManifest, fieldTileUrl, refreshCities, refreshWind, annotateCustom, observedRange } from './data.ts';
import { persist as persistState, loadPersisted, validSeed, bootBegin, bootDone, getCountries } from './persist.ts';
import type { CountryRow } from './persist.ts';
import { baseStyle, detectBasemap, DETAIL_ZOOM, LAND_URL } from './map/style.ts';
import { LIGHT_SCHEME, MAP_THEMES, mapTheme, applyMapTheme, isLight, themePick, setThemePick } from './map/theme.ts';
import type { ThemePick } from './map/theme.ts';
import { visitorLocation } from './locale.ts';
import type { BasemapCaps } from './map/style.ts';
import { addWindLayer, arrowSwatch, ARROW_BINS, CALM_MAX } from './map/wind-sprites.ts';
import { windActive, windAvailable, setWindAvailable } from './wind-state.ts';
import { createFieldMachine } from './field/crossfade.ts';
import type { FieldHost, FieldRevealMode } from './field/crossfade.ts';
import type { AppState, CityCollection, ViewName } from './state.ts';

// Data-source attribution, revealed by the city band's +/- toggle. Belongs to the
// map rather than to app state — it is set once, when the basemap is chosen.
let mapCredits = '';

// ---------------------------------------------------------------------------
// Map setup
// ---------------------------------------------------------------------------

function localeView(): { center: [number, number]; zoom: number } {
  const { lon, lat } = visitorLocation();
  return { center: [lon, lat], zoom: 2.5 };
}

function initMap(caps: BasemapCaps): Promise<unknown> {
  const lv = localeView();
  setMap(new maplibregl.Map({
    container: 'map',
    style: baseStyle(caps),
    center: lv.center,
    zoom: lv.zoom,
    minZoom: 0.8,
    maxZoom: 8,
    renderWorldCopies: true,
    attributionControl: false,
    dragRotate: false,
  }));
  (window as unknown as { __weatherMap: MapLibreMap }).__weatherMap = map; // debug/verification handle
  map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'bottom-right');
  // ODbL obliges the OpenStreetMap credit whenever the self-hosted basemap is in play;
  // the rest is courtesy. Natural Earth is public domain.
  const credits = ['Data: NOAA NCEI ISD Global-Hourly', 'Basemap: Natural Earth'];
  if (caps.vector) credits.push('© OpenStreetMap contributors (ODbL)');
  if (caps.dem) credits.push('Elevation: Mapzen/AWS Terrain Tiles');
  credits.push('Cities: SimpleMaps (CC BY 4.0)', 'Labels: Noto Sans (OFL)');
  mapCredits = credits.join(' · ');
  // Both views reveal the credits behind a "+" (see #map-foot / #cb-foot), so we
  // skip MapLibre's own always-expanded AttributionControl — the string is too
  // long to sit permanently across the map.
  return new Promise((resolve) => map.on('load', resolve));
}

// Where field layers get inserted: above the ground (land tint, terrain, landuse) and
// below everything that has to stay on top of the weather — inland water first, then
// the ocean mask that clips the field to the coast, then the coastline itself.
function fieldBeforeId(): string | undefined {
  for (const id of ['basemap-water', 'ocean-mask-fill', 'land-outline']) {
    if (map.getLayer(id)) return id;
  }
  return undefined;
}

// Build the fill layer for one frame. Starts fully transparent: a frame is only
// revealed once every tile it needs has loaded (see field/crossfade.ts).
function fieldLayerSpec(layerId: string, sourceId: string): LayerSpecification {
  return {
    id: layerId,
    type: 'fill',
    source: sourceId,
    'source-layer': 'field',
    layout: { visibility: state.fieldOn ? 'visible' : 'none' },
    paint: {
      'fill-color': colorExpressionOn(state.variable, 'v'),
      'fill-opacity': 0,
      // Transition-less on purpose: every fade is driven per rAF tick by the
      // crossfade machine with explicit opacity values, so MapLibre's own easing
      // must never stack a second interpolation on top — and the instant path
      // relies on both frames swapping in the same render.
      'fill-opacity-transition': { duration: 0, delay: 0 },
      // No antialiasing: the field is built from many per-cell isoband polygons,
      // and antialiased edges on each cell render as a visible square grid at
      // partial opacity. Solid fills keep same-band cells seamless while the
      // band-to-band color changes still read as contours.
      'fill-antialias': false,
    },
    // The `-transition` paint keys are valid style-spec but are not part of the
    // typed paint record, so the literal needs the assertion.
  } as LayerSpecification;
}

// The field's frame swapping lives in field/crossfade.ts, behind this adapter —
// the only part of it that knows about MapLibre. Everything above the adapter is
// the state machine, which is why it can be tested without a map.
const fieldHost: FieldHost = {
  addFrame(sourceId, layerId, url) {
    const grid = state.manifest!.grid;
    map.addSource(sourceId, { type: 'vector', tiles: [url], minzoom: 0, maxzoom: (grid && grid.maxzoom) || 5 });
    map.addLayer(fieldLayerSpec(layerId, sourceId), fieldBeforeId());
  },
  removeLayer: (id) => map.removeLayer(id),
  removeSource: (id) => map.removeSource(id),
  hasLayer: (id) => !!map.getLayer(id),
  hasSource: (id) => !!map.getSource(id),
  getOpacity(id) {
    const v = map.getPaintProperty(id, 'fill-opacity');
    return typeof v === 'number' ? v : null;
  },
  setOpacity: (id, v) => map.setPaintProperty(id, 'fill-opacity', v),
  setOpacityTransition: (id, ms) => map.setPaintProperty(id, 'fill-opacity-transition', { duration: ms, delay: 0 }),
  setVisibility: (id, visible) => map.setLayoutProperty(id, 'visibility', visible ? 'visible' : 'none'),
  setColor: (id, color) => map.setPaintProperty(id, 'fill-color', color as ExpressionSpecification),
  isReady(sourceId) {
    if (!map.isSourceLoaded(sourceId)) return false;
    // isSourceLoaded also reads true before the first tiles are even requested, so
    // require real geometry too. On a fresh source it can only be this frame's.
    try {
      return map.querySourceFeatures(sourceId, { sourceLayer: 'field' }).length > 0;
    } catch {
      return false;
    }
  },
  watchSource(sourceId, fn) {
    const onData = (e: MapSourceDataEvent) => {
      if (e.sourceId === sourceId && e.dataType === 'source') fn();
    };
    map.on('data', onData);
    return () => map.off('data', onData);
  },
  raf: (fn) => requestAnimationFrame(fn),
  cancelRaf: (h) => cancelAnimationFrame(h),
  setTimer: (fn, ms) => setTimeout(fn, ms),
  clearTimer: (h) => clearTimeout(h as ReturnType<typeof setTimeout>),
};

const field = createFieldMachine(fieldHost, {
  enabled: () => state.fieldOn,
  opacity: () => state.fieldOpacity,
  url: () => fieldTileUrl(),
});

// Kept as a free function because it reads as a verb at ~10 call sites, and
// because the default reveal mode belongs to the machine, not to each caller.
function updateField(mode?: FieldRevealMode) {
  field.update(mode);
}

// Fill in the land, the mask cut from it, and the islands the mask had to give up.
// ---------------------------------------------------------------------------
// Rendering a frame
// ---------------------------------------------------------------------------
// `explicit` (the default) means a user or a URL asked for this date, so the
// address bar should carry the date slug. The two startup calls that only seed
// "today" pass explicit:false, so a bare canonical URL stays bare on load.
// Move the year to a section. Everything that follows from a new date is a
// `dateIdx` subscriber, so this states the change and stops — it does not need
// to know what is downstream of it, which is the whole point of the store.
function showFrame(idx: number, { explicit = true }: { explicit?: boolean } = {}) {
  if (explicit) markDateExplicit();
  const next = (idx + state.dates.length) % state.dates.length;
  // Counted only when a reader moved time on purpose, and only where they let
  // go. The ruler is a native range input, so one drag across the year arrives
  // here up to 36 times; playback arrives every three seconds for as long as it
  // is left running. Neither is a decision, and both would drown the real ones.
  if (explicit && !state.playing) {
    trackDebounced('date_change', { section: state.dates[next] ?? '' }, 600);
  }
  store.set({ dateIdx: next });
}

// Everything the current date drives. The internal order is load-bearing — the
// ruler's marker is placed after the field has been asked for — so it stays one
// function rather than five listeners.
function renderDate() {
  updateField();
  refreshCities();
  refreshWind();
  renderSentence(); // "mid July" is one of the sentence's three words
  updateRulerMarker();
}

function wireStateSubscriptions() {
  store.subscribe(['dateIdx'], renderDate);
  store.subscribe(['variable'], renderVariable);
  store.subscribe(['bucket'], renderBucket);
  store.subscribe(['recipeCanon'], renderRecipe);
  // A banded legend is a fixed key; a continuous one is clipped to the values
  // actually loaded, so it re-reads whenever the frame's cities change.
  store.subscribe(['cityFC'], () => {
    if (VAR[state.variable] && !VAR[state.variable].banded) updateLegend();
  });
  // Which summoned surface is open. Three renderers, one fact: the sentence
  // shows/hides/anchors the box and lights its word, the lens menu fills its
  // contents, the Layers panel is its own. Order matters only in that the lens
  // menu must draw before the sentence measures where to hang it.
  store.subscribe(['menu'], () => { renderLensMenu(); renderMenus(); renderLayerMenu(); renderSearchMenu(); renderDayMenu(); });
  // The corner words light whichever surface or page is open.
  store.subscribe(['menu', 'peaksOpen', 'variable', 'daySeed', 'compare', 'searchFor'], renderCornerWords);
  // A day names the rating ("days like Split") in the sentence and the key.
  store.subscribe(['daySeed'], () => { renderSentence(); updateLegend(); });
  store.subscribe(['compare'], renderTray);
  // In the city view the charts track the scrubbed/played day too, so the day
  // markers follow the progress bar. A subscription rather than a call out of
  // the header label, which has no business knowing the city panel exists.
  store.subscribe(['dateIdx'], () => {
    if (state.view === 'city') updateCityCharts();
  });
  // The briefing states a verdict, so it follows whatever changes the verdict:
  // which rating is showing, and the recipe behind the custom one.
  store.subscribe(['variable', 'recipeCanon'], updateCityCharts);
}

// `explicit` (the default) means a user or a URL chose this field, so the mode
// slug belongs in the address bar. The startup call that restores the default
// field passes explicit:false so a bare canonical URL stays bare on load.
function applyVariable(v: VarKey, { explicit = true }: { explicit?: boolean } = {}) {
  if (explicit) markModeExplicit();
  // Selecting the custom score by ANY route — chip, restored session, a
  // /my-kind-of-day deep link — counts as having seen it, so make sure its
  // chrome exists.
  if (VAR[v] && VAR[v].custom) unlockCustom();
  // Same explicit test the address bar uses: a restored field is not a choice.
  if (explicit && v !== state.variable) track('layer_change', { variable: v });
  // Patched in the same set as the variable, so a subscriber never sees the new
  // variable against the old group memory.
  store.set({ variable: v, ...groupMemory(v) });
}

// Remember a variable as its group's last-chosen view (see lastTemp/lastWet in
// state.ts). Not persisted, so startup seeds it from whatever was restored.
function groupMemory(v: VarKey): Partial<AppState> {
  if (VARIANT_GROUPS.temp.members.some(([k]) => k === v)) return { lastTemp: v };
  if (VARIANT_GROUPS.wet.members.some(([k]) => k === v)) return { lastWet: v };
  return {};
}

// Everything a new field follows from. Registered as the `variable` subscriber,
// and called directly once at startup — where the restored variable is already
// in state, so there is no change for the store to notice and the first paint
// would otherwise never happen.
function renderVariable() {
  const v = state.variable;
  // Field: repoint tiles at the new variable. The old metric's colours ease out to
  // empty and the new metric's fade in over them, rather than the old geometry being
  // recoloured in place with the new palette (which would flash wrong-for-its-data
  // colours). Any frame still loading keeps its own variable's palette until it lands.
  field.fadeOutCurrent();
  field.update('fadein');
  field.setColor(colorExpressionOn(v, 'v'));
  // Dots + labels: recolor and refilter. Every variable is already in hand, so
  // this is a repaint — no refetch needed.
  const expr = colorExpression(v);
  for (const id of ['weather-glow', 'weather-points']) {
    map.setFilter(id, dotFilter(v));
    map.setPaintProperty(id, 'circle-color', expr);
  }
  // Names stay a zoom behind the dots here too — switching variable must not quietly
  // put all of them back — save the open city's, which labelFilter always keeps.
  if (map.getLayer('city-labels')) map.setFilter('city-labels', labelFilter(v));
  updateLegend();
  renderSentence();
  if (state.manifest) persist(); // remember the chosen layer across reloads
  // The peaks rail follows the lens: a rating gets a shortlist, a plain weather
  // layer has none (the hottest places on Earth are not a holiday list).
  refreshPeaks({ immediate: true });
}

// ═══════════════════════════════════════════════════════════════════════════
// UI: chrome (header + bottom bar), landing/search, and the city view.
// The map machinery above is unchanged; this half is the redesigned frame around it.
// ═══════════════════════════════════════════════════════════════════════════

function setBucket(bk: string) {
  if (bk !== state.bucket) track('bucket_change', { bucket: bk });
  store.set({ bucket: bk });
}

// Everything a new time-of-day follows from. The store skips a set that
// changes nothing.
function renderBucket() {
  renderSentence();
  updateField();
  refreshCities();
  refreshWind();
  persist();
  refreshPeaks({ immediate: true }); // peaks are per time-of-day too
  // A city briefing is per-bucket, so switching the time of day reloads its series.
  if (state.view === 'city' && state.selectedCity) reloadSeries();
}

// A search hit, opened as a briefing.
function openCityHit(s: CityHit) {
  openCity(s.id, { name: s.name, nameAscii: s.nameAscii, country: s.country, admin: s.admin, citySlug: s.citySlug, placeSlug: s.placeSlug, pop: s.pop, lon: s.lon, lat: s.lat });
}

// What the atlas is, in one sentence, before a reader has asked.
//
// Built from the manifest rather than written into the markup: the length of the
// record and the station count are facts about the data that was actually baked,
// and a prose "eleven years, 13,000 stations" would quietly go stale the first
// time another year is merged.
//
// The LENGTH, not the endpoints — see year-span.ts. The blend keeps a rolling
// decade, so "a decade" stays true every merge, where "(2015–2025)" advertised
// an edge and had to be rewritten annually.
function renderLandingNote() {
  const m = state.manifest;
  if (!m) return;
  const stations = m.stationCount ? `${Math.round(m.stationCount / 1000)},000 stations` : 'thousands of stations';
  $('landing-note').textContent =
    `${yearsPhraseCap(m.years || []) || 'Years'} of hourly observations from ${stations}, `
    + 'blended into one representative year — read as what a date is usually like, not a forecast.';
}

// Closing a briefing. Delegated for the same reason as the "Why?" toggle: the
// masthead is rebuilt from a string on every calendar hover, so a listener bound
// to the button would be thrown away several times a second.
function wireCloseCity() {
  document.addEventListener('click', (e) => {
    if ((e.target as HTMLElement | null)?.closest('[data-close-city]')) goMap();
  });
}

// The score details' one toggle, delegated.
//
// "Why?" appears in three transient surfaces and they are all rebuilt from
// strings, so binding a listener per button would mean rebinding on every
// repaint. One document-level handler on [data-why] outlives all of them — and
// because the three read the SAME flag, flipping it here is the whole action;
// what follows is only redrawing whatever happens to be on screen.
function wireWhyToggle() {
  document.addEventListener('click', (e) => {
    if (!(e.target as HTMLElement | null)?.closest('[data-why]')) return;
    state.whyExpanded = !state.whyExpanded;
    persist();
    rerenderPeaks();
    if (state.view === 'compare') renderCompare();
    // The tooltip is redrawn on the next hover; there is nothing to update while
    // the pointer is off a dot.
  });
}

// "Find your day", the corner word. On the landing it IS the sentence, so it
// opens the sentence's first word; with a day already made it brings that day's
// rating and panel back; otherwise it goes to the landing to make one.
function findYourDay() {
  if (state.view === 'landing') {
    store.set({ menu: 'day-place' });
    return;
  }
  if (state.daySeed) {
    if (state.view !== 'map') goMap();
    applyVariable('custom');
    setPeaksOpen(true);
    return;
  }
  goLanding();
  store.set({ menu: 'day-place' });
}

// "Compare places", the corner word. With a briefing open the first place is
// already chosen; otherwise the picker starts from whatever is pending.
function comparePlaces() {
  if (state.view === 'compare') {
    startCompareFrom(null);
    return;
  }
  const sel = state.selectedCity;
  if (state.view === 'city' && sel && sel.name) {
    startCompareFrom(placeFrom({ id: sel.id, name: sel.name, country: sel.country || '', placeSlug: sel.placeSlug || state.series?.placeSlug, lat: sel.lat, lon: sel.lon }));
  } else {
    startCompareFrom(null);
  }
}

// Arrive on the map with the camera where an answer reads — a day's panel wants
// the world, centred on where the day was.
function showMap(center: [number, number], zoom: number, animate: boolean) {
  if (state.view !== 'map') {
    setSelectedHighlight(null);
    setView('map');
  }
  map.resize();
  if (animate) map.flyTo({ center, zoom, duration: 900 });
  else map.jumpTo({ center, zoom });
}


// ═══════════════ The "My kind of day" recipe ═══════════════
let recipeFieldTimer: ReturnType<typeof setTimeout> | null = null;
// Whether the NEXT recipe render should defer its tile refetch. Set immediately
// before the store write that triggers it, so a slider drag costs one fetch at
// the end rather than one per tick.
let recipeDefer = false;

// The one mutator for the "My kind of day" recipe. Everything downstream hangs
// off state the rest of the app already re-reads, so an edit is: clamp, restate
// the canonical string, and let the subscribers do the rest.
function setRecipe(r: RecipeInput | null | undefined, { defer = false }: { defer?: boolean } = {}) {
  recipeDefer = defer;
  const recipe = clampRecipe(r);
  // `defer` already means "this is a tick of a drag, not the end of one" — the
  // same distinction the tile refetch below is built on. Only the settled recipe
  // is a decision worth counting.
  if (!defer) track('recipe_commit');
  store.set({ recipe, recipeCanon: encodeRecipe(recipe) });
}

// Everything a changed recipe follows from. Dots and calendar repaint
// immediately from data already in hand; the tile refetch is debounced.
function renderRecipe() {
  // Nothing to publish here: the tooltip and calendar driver chips resolve the
  // live factors through factorsFor(key, recipe) from the state they are handed,
  // so there is no registry entry to keep in step.
  const src = (map && map.getSource ? map.getSource('weather') : null) as GeoJSONSource | null | undefined;
  if (src && state.cityFC) {
    annotateCustom(state.cityFC);
    src.setData(state.cityFC);
  }
  if (state.series) {
    computeCustomSeries(state.series);
    if (state.view === 'city') buildCalendar(state.series);
  }
  persist();
  if (state.variable === 'custom') {
    if (recipeFieldTimer) clearTimeout(recipeFieldTimer);
    const wait = recipeDefer ? 250 : 0;
    recipeFieldTimer = setTimeout(() => {
      recipeFieldTimer = null;
      updateField();
    }, wait);
    // The rail re-ranks against the edited recipe; it debounces on its own, so
    // a slider drag costs one scan, not one per pixel.
    refreshPeaks();
  }
}

// ── deep-link URL sync ───────────────────────────────────────────────────────
// The address bar mirrors what a reader shares: date / map field, plus either a
// city (location view, /{country}/{city}/…) or the exact map camera (map view,
// /@{lat},{lng},{zoom}/…). Remembering the camera — not just a country — means
// Back/Forward restores the very view the reader left, so the map slides back
// smoothly instead of snapping to a country centre. The bare "/" is the landing
// search. The vocabulary is in lib/slug.ts, the rules about which parts belong
// in the bar are in lib/route-state.ts, and the plumbing is in
// client/routing/url-sync.ts.

// "Remember this" — to storage AND to the address bar.
//
// Two mirrors of the same state, kept as one call because every mutator wants
// both and forgetting either is invisible until a reload or a shared link is
// wrong. The two halves do not know about each other: persistState() is in
// persist.ts and writes localStorage, syncUrl() is below and writes history.
// This is the seam where the composition root says they go together.
function persist() {
  persistState();
  syncUrl();
}

// The compare view's band is a locator, not a weather map: it shows where the
// places are and nothing else. The field, the arrows and every other city's dot
// step aside while it is up — and come back as the reader had them.
function quietMap(on: boolean) {
  field.setVisible(!on && state.fieldOn);
  for (const id of ['weather-glow', 'weather-points', 'city-labels', 'wind-field']) {
    if (!map.getLayer(id)) continue;
    const show = !on && (id !== 'wind-field' || windActive());
    map.setLayoutProperty(id, 'visibility', show ? 'visible' : 'none');
  }
}

function setFieldVisible(on: boolean) {
  track('layer_toggle', { layer: 'field', on });
  state.fieldOn = on; // read back by the machine, so it goes first
  field.setVisible(on);
  $('toggle-field').classList.toggle('active', on);
}
function setWindOn(on: boolean) {
  track('layer_toggle', { layer: 'wind', on });
  state.windOn = on;
  $('toggle-wind').classList.toggle('active', on);
  syncWind();
}
function syncWind() {
  if (!map.getLayer('wind-field')) return;
  map.setLayoutProperty('wind-field', 'visibility', windActive() ? 'visible' : 'none');
  refreshWind();
  updateArrowLegend();
}
function setFieldOpacity(v: number) {
  state.fieldOpacity = v;
  field.setOpacity(v);
}
function setCitiesOn(on: boolean) {
  track('layer_toggle', { layer: 'cities', on });
  state.citiesOn = on;
  $('toggle-cities').classList.toggle('active', on);
  refreshCities();
}

// Every widget is wired by the module that owns it; what stays here is the
// wiring that genuinely spans modules.
function wireControls() {
  wireChrome();
  wireSentence();
  wireWhyToggle();
  wireCloseCity();
  wireLayout();
  // The ruler's marker sweeps with playback rather than jumping dekad to dekad:
  // the data steps, but time does not, and the marker is a clock.
  wirePlayback({ showFrame, onProgress: updateRulerSweep });
  wireRuler({ pause, showFrame, persist });
  wireHistory();
  wireCustomEditor();
  wireSearchMenu();
  wireDaySentence();
  wireCompare();

  $('find-day').addEventListener('click', (e) => { e.stopPropagation(); findYourDay(); });
  $('compare-open').addEventListener('click', (e) => { e.stopPropagation(); comparePlaces(); });
  // The briefing's "Compare Split with…", delegated: the masthead is rebuilt
  // from a string on every calendar hover.
  document.addEventListener('click', (e) => {
    if (!(e.target as HTMLElement | null)?.closest('[data-compare-city]')) return;
    e.stopPropagation();
    comparePlaces();
  });

  // The theme turned under an open page — the system's scheme changed while on
  // Auto, or the reader picked one. The stylesheet follows by itself; the map's
  // ground has to be repainted, and a field wash still at the old theme's
  // default moves to the new one's.
  let lightNow = isLight();
  const themeChanged = () => {
    if (isLight() === lightNow) { renderThemeSeg(); return; }
    lightNow = isLight();
    applyMapTheme();
    const other = lightNow ? MAP_THEMES.dark : MAP_THEMES.light;
    if (state.fieldOpacity === other.fieldOpacity) {
      setFieldOpacity(mapTheme().fieldOpacity);
      $<HTMLInputElement>('field-opacity').value = String(Math.round(state.fieldOpacity * 100));
    }
    if (state.view === 'compare') openCompare(state.compare.places, { animate: false });
    renderThemeSeg();
  };
  const renderThemeSeg = () => {
    const pick = themePick();
    document.querySelectorAll<HTMLElement>('#theme-seg [data-theme-pick]').forEach((b) => {
      const on = b.dataset.themePick === pick;
      b.classList.toggle('active', on);
      b.setAttribute('aria-checked', String(on));
    });
    // The corner's own switch names the room it leads to. Its visible word is
    // the stylesheet's (#theme-toggle::before), so it is there before this runs.
    $('theme-toggle').setAttribute('aria-label', `Switch to ${isLight() ? 'dark' : 'light'} theme`);
  };
  LIGHT_SCHEME.addEventListener('change', () => { if (themePick() === 'auto') themeChanged(); });
  $('theme-seg').addEventListener('click', (e) => {
    const b = (e.target as HTMLElement | null)?.closest<HTMLElement>('[data-theme-pick]');
    if (!b) return;
    track('theme_pick', { theme: b.dataset.themePick! });
    setThemePick(b.dataset.themePick as ThemePick);
    themeChanged();
  });
  // The visible switch, in the corner on every view: one click to the other
  // room. Auto stays in Layers for a reader who wants to hand it back.
  $('theme-toggle').addEventListener('click', () => {
    const to: ThemePick = isLight() ? 'dark' : 'light';
    track('theme_pick', { theme: to });
    setThemePick(to);
    themeChanged();
  });
  renderThemeSeg();

  // A viewport change refetches what the viewport decides (cities, wind) and, in
  // the map view, renames the country in the URL.
  map.on('moveend', () => {
    refreshCities();
    refreshWind();
    if (state.view === 'map') syncUrl();
  });

  // The global keyboard map — it dispatches across playback, layout and the
  // popovers, which is why it lives in the root and not with any one of them.
  window.addEventListener('keydown', (e) => {
    // Don't steal typing in the search box.
    if (e.target && ((e.target as HTMLElement).tagName === 'INPUT' || (e.target as HTMLElement).tagName === 'TEXTAREA')) {
      if (e.code === 'Escape') (e.target as HTMLElement).blur();
      return;
    }
    if (e.code === 'Escape') {
      // Every summoned surface, the editor included, closes through state.menu
      // (sentence.ts owns that handler); what is left here is the city view.
      if ((state.view === 'city' || state.view === 'compare') && !state.menu) goMap();
    } else if (e.code === 'Space') {
      e.preventDefault();
      togglePlay();
    } else if (e.code === 'ArrowRight') {
      pause();
      showFrame(state.dateIdx + 1);
      syncUrl();
    } else if (e.code === 'ArrowLeft') {
      pause();
      showFrame(state.dateIdx - 1);
      syncUrl();
    }
  });

  // "My kind of day" — the recipe seeder inside the editor and the peaks panel
  // beside the map. It gets exactly the hooks it needs, so neither module
  // imports the other.
  initMyDay({
    openCity,
    showFrame,
    pause,
    wireAutocomplete,
    unlockCustom,
    persist,
    setBucket,
    applyVariable,
    setRecipe,
    syncCustomEditor,
    showMap,
    openCompare: (places) => { openCompare(places); },
    toggleCompare: toggleInComparison,
  });
}

function pickInitialDate() {
  const n = state.dates.length;
  if (!n) return 0;
  const now = new Date();
  const md = `${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  // Which of the 36 sections today falls in.
  const exact = state.dates.indexOf(sectionKeyForMmdd(md));
  if (exact >= 0) return exact;
  // Fallback (older daily timeline, or a section missing): nearest by day-of-year,
  // taking a section's midpoint (month, dekad -> ~day 5/15/25) as its position.
  const doyOf = (m: number, d: number) => Date.UTC(2000, m - 1, d) / 86400000;
  const posOf = (s: string) => {
    const [a, b] = s.split('-').map(Number);
    return s.length <= 4 ? doyOf(a, [5, 15, 25][b - 1] || 25) : doyOf(a, b);
  };
  const today = doyOf(now.getMonth() + 1, now.getDate());
  let best = 0;
  let bestDist = Infinity;
  state.dates.forEach((s, i) => {
    const dist = Math.abs(posOf(s) - today);
    if (dist < bestDist) { bestDist = dist; best = i; }
  });
  return best;
}


async function main() {
  try {
    await loadManifest();
  } catch (err) {
    $('loading').innerHTML =
      'No weather data loaded yet.<br><small style="opacity:.7">If a rebuild is running it will appear shortly — otherwise run <code>npm run preprocess</code>. This page auto-retries.</small>';
    console.error(err);
    setTimeout(() => location.reload(), 15000);
    return;
  }
  setWindAvailable(!!(state.manifest!.grid && state.manifest!.grid.windVars));
  state.windOn = state.windOn && windAvailable();

  // Restore the last day / bucket / variable (never a stale city). Guarded: a
  // boot that died on restored state last time comes up on defaults instead of
  // dying the same way again (see bootBegin).
  if (bootBegin()) console.warn('Previous load did not finish — restored settings discarded.');
  const saved = loadPersisted();
  if (saved) {
    if (saved.bucket && state.manifest!.buckets.some((b) => b.key === saved.bucket)) state.bucket = saved.bucket;
    if (saved.variable && VAR[saved.variable as VarKey]) state.variable = saved.variable as VarKey;
    if (saved.peaksOpen === true) state.peaksOpen = true;
    if (saved.whyExpanded === true) state.whyExpanded = true;
    state.daySeed = validSeed(saved.daySeed);
    if (saved.compareFull === true) state.compare = { places: [], full: true };
    const cu = saved.compareUi;
    if (cu && Array.isArray(cu.open) && Array.isArray(cu.split)) {
      state.compareUi = { open: cu.open.filter((x) => typeof x === 'string'), split: cu.split.filter((x) => typeof x === 'string'), table: cu.table === true };
    }
    const savedRecipe = saved.recipe && decodeRecipe(saved.recipe);
    if (savedRecipe) {
      state.recipe = clampRecipe(savedRecipe);
      state.recipeCanon = encodeRecipe(state.recipe);
    }
    // Was the custom score ever theirs? The flag decides; two legacy tells fill
    // in for blobs written before it existed: having it selected, or a persisted
    // recipe that differs from the default (the default is written untouched on
    // every persist, so only an edit can make it differ).
    state.customOn =
      saved.customOn === true ||
      saved.variable === 'custom' ||
      (!!savedRecipe && encodeRecipe(clampRecipe(savedRecipe)) !== encodeRecipe(DEFAULT_RECIPE));
  }

  // Today's section, seeded the moment the calendar keys exist and before ANY
  // render can read it. dateIdx starts at 0, and every surface that names the date
  // — the sentence's middle word above all — would otherwise paint "early January"
  // first and be corrected to today several hundred milliseconds later, once the
  // map has finished initialising and renderDate runs. On a pre-rendered page that
  // correction is the most visible thing on the screen, because everything around
  // it was already right.
  //
  // Assigned rather than set through the store: this is the initial value, not an
  // edit. `store.set` would also notice no change on the one day of the year when
  // today's section IS index 0, and the first frame would never draw.
  state.dateIdx = pickInitialDate();

  $<HTMLInputElement>('ruler-range').max = String(Math.max(0, state.dates.length - 1));

  // The field's wash is a touch lighter on a light page, where the same 0.6
  // reads heavier against pale ground.
  state.fieldOpacity = mapTheme().fieldOpacity;

  // Hand the extracted modules what they need from the app, BEFORE anything can
  // call into them. Each takes only the decisions that are genuinely the app's:
  // opening a city moves the map and writes the URL; switching view persists.
  initCityPanel({
    setView, markUserCamera, persist, activityKeys, showFrame, pause, togglePlay, applyVariable,
    setBucket, setRecipe, unlockCustom, goMap,
  });
  initLayout({ closeLayerMenu, persist, markUserCamera, pause });
  initChrome({
    persist,
    credits: () => mapCredits,
    setFieldVisible, setCitiesOn, setWindOn, setFieldOpacity,
  });
  initSentence({ setBucket, showFrame, pause, persist });
  initSearchMenu({ wireAutocomplete, openCityHit, addToComparison });
  initDaySentence({ wireAutocomplete, openSearch: () => openSearchPanel('open') });
  initCompare({ setView, persist, showFrame, pause, applyVariable, activityKeys, openCity, copyLink, quietMap });
  initLensMenu({
    applyVariable,
    // Entering step 2 IS choosing the custom rating: the editor is a view onto
    // a recipe the map is painting, and editing one the map is not showing
    // would be the "which of these am I looking at" confusion the sentence
    // exists to remove.
    openRecipeEditor: () => {
      unlockCustom();
      applyVariable('custom');
      toggleCustomEditor();
    },
  });
  initRecipeEditor({ setRecipe });
  initUrlSync({ setRecipe, applyVariable, showFrame });

  renderSentence();
  renderLandingNote();
  renderCornerWords();
  buildRuler();

  await initMap(await detectBasemap());
  try {
    await addOceanMask();
  } catch (err) {
    console.warn('Ocean mask unavailable — field will not be clipped to land.', err);
  }
  addBasemapDetailLayers();
  addWeatherLayers();
  addCountryLabels();
  setupHover({ onOpenCity: openCity });
  wireControls();
  wireStateSubscriptions();

  // Mute the URL writer through the default-state setup below: these persist()
  // calls would otherwise rewrite the address bar to "/" and clobber the deep
  // link before applyRouteFromUrl() gets to read it. It is unmuted there.
  setApplyingRoute(true);
  // explicit:false — restoring the default field and seeding "today" are not the
  // reader choosing them, so a bare canonical URL stays neutral in the bar.
  if (VAR[state.variable] && VAR[state.variable].custom) unlockCustom();
  store.set(groupMemory(state.variable)); // seed the group memory the restored variable implies
  // dateIdx is already today's section — seeded up by loadManifest, before the
  // first renderSentence, so no surface ever shows a date that is about to change.
  // These paint the restored field and that date once, now that the map exists.
  renderVariable();
  renderSentence();
  renderDate();

  // The timeline never auto-plays; it starts on today and only moves when the user
  // presses play or scrubs.
  //
  // A pre-rendered city page is ALREADY the city view — the server sent it with
  // body.view-city and the briefing in place. Seeding a default view here would
  // flip it to the landing and straight back a few milliseconds later, when
  // applyRouteFromUrl opens the city: the map band jumps from 288px to the full
  // 720, the briefing collapses to nothing, and both snap back. That is the
  // "loads at the right size, resizes, resizes back" flicker on a direct city
  // URL. The route decides the view on any deep link, so the default is only
  // worth seeding when there is no pre-rendered one to respect.
  //
  // Assigned rather than passed through setView, for the same reason dateIdx is
  // above: this is the initial value, not an edit, and nothing is listening yet.
  // openCity calls setView('city') presently, which finds the class already set
  // and the map box already where it belongs, so its animation is a no-op.
  if (document.body.dataset.prerenderCity != null) state.view = 'city';
  else setView(saved && saved.view === 'map' ? 'map' : 'landing');
  pause();

  // A deep link in the address bar overrides the restored defaults above — it may
  // open a city, focus a country on the map, and set the date/field. No link (the
  // bare "/") leaves the landing/today defaults untouched. A shared "My kind of
  // day" needs nothing extra here: ?r= reconstructs the recipe, and the peaks
  // rail ranks whatever rating that leaves showing.
  await applyRouteFromUrl();
  refreshPeaks({ immediate: true });

  // The guides' top-row words lead here: "/#search" and "/{place}/#compare" ask
  // for the surface a document cannot open itself. The hash is spent once read.
  if (location.hash === '#search' || location.hash === '#compare') {
    const want = location.hash;
    history.replaceState(history.state, '', location.pathname + location.search);
    if (want === '#search') openSearchPanel('open');
    else comparePlaces();
  }

  $('loading').hidden = true;
  bootDone();
}

// main() used to be fired and forgotten, so anything it threw past the manifest
// step became an unhandled rejection: no message, no overlay, and a page that
// looked half-built with no clue why. Say it out loud, and get the overlay out of
// the way of whatever DID come up — the next load will drop the restored state
// that most likely caused it (bootBegin) and come back clean.
main().catch((err) => {
  console.error('Startup failed', err);
  $('loading').hidden = true;
});
